import { NumberType_Enum } from '@weave/schema-gen-ts/dist/schemas/comm/phone-number/v1/phone_number_service.pb';
import { Channel } from '@weave/schema-gen-ts/dist/schemas/comm-preference/shared/v1/enums.pb';
import { Link } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { MessageType_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/enums.pb';
import { DynamicFieldKey_Enum } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { ClientSettingsApi, ClientSettingsQueries } from '@frontend/api-client-settings';
import { useGenerateManagementUrls } from '@frontend/api-email-composer';
import { BulkMessagesQueries } from '@frontend/api-messaging';
import { FeatureAccessFlags, FeatureAccessPermissions, useFeatureAccess } from '@frontend/feature-access';
import { useTranslation } from '@frontend/i18n';
import { useLocationAddress } from '@frontend/location-address';
import { usePracticeName } from '@frontend/practice-name';
import { formatDynamicFieldKey } from '@frontend/string';
import { phone as formatPhone, useAlert } from '@frontend/design-system';
import { useContentComposer } from '../../content-composer/content-composer-provider';
import type { FooterLinkType } from '../../content-composer/types';
import { useGetOnlineSchedulingLink } from '../queries';
import { createDocumentLink } from '../utils/create-document-link';
import { getWeaveFormDynamicField } from '../utils/get-weave-form-dynamic-field';
import { useDocuments } from './use-documents';

const MOCK_PREFERENCE_TEST_EMAIL = 'weavetestemailpreferences@getweave.com';

export const useLinks = () => {
  const alert = useAlert();
  const { t } = useTranslation('bulk-messaging');
  const { composerMode, selectedLocationId: locationId, textDynamicFields } = useContentComposer();

  //////////////////////////////////////// BUSINESS_GROUP_ADDRESS /////////////////////////////////////////////////////
  const { addressLink, humanReadableAddress } = useLocationAddress(locationId);
  const getAddress = (mode?: string) => {
    if (mode === 'preview') {
      return humanReadableAddress;
    }

    return formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_ADDRESS);
  };
  const getAddressLink = (mode?: string) => {
    if (mode === 'preview') {
      return addressLink;
    }

    return formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_ADDRESS_LINK);
  };

  ////////////////////////////////////////// BUSINESS_GROUP_EMAIL /////////////////////////////////////////////////////
  // This is only used in bulk messaging
  const { locationsWithEnabledFlag } = useFeatureAccess(
    FeatureAccessFlags.emailMarketing,
    FeatureAccessPermissions.emailMarketing
  );
  const {
    querySetKeys: { sets, keys },
  } = ClientSettingsApi;
  const { data: email = '' } = ClientSettingsQueries.useGetSetting(sets.email, keys.email, {
    enabled: locationsWithEnabledFlag.length > 1,
    onError: () => {
      alert.error(t('Failed to get reply-to email address.'));
    },
  });

  //////////////////////////////////////// BUSINESS_GROUP_NAME ///////////////////////////////////////////////
  const practiceName = usePracticeName(locationId, 'business name');
  const getBusinessName = (mode?: string) => {
    if (mode === 'preview') {
      return practiceName;
    }

    return formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_NAME);
  };

  //////////////////////////////////////// BUSINESS_GROUP_PHONE ///////////////////////////////////////////////////////
  const { data: phone = '' } = BulkMessagesQueries.useDefaultPhoneRecord({
    locationId,
    numberType: NumberType_Enum.SMS,
  });
  const formattedPhone = formatPhone(phone, { hideCountry: true, format: 'numbers' });

  //////////////////////////////////////// CALL_TO_ACTION_LINK //////////////////////////////////////////////
  const getCallToActionLink = (mode?: string) => {
    if (mode === 'preview') {
      return 'https://wv3.io/wf0001';
    }

    return formatDynamicFieldKey(DynamicFieldKey_Enum.CALL_TO_ACTION_LINK);
  };

  /////////////////////////////////////////////// FORMS_LINK / FORMS_LINKS ////////////////////////////////////////////////////
  const { getDocument } = useDocuments();
  const createFormLink = (link: Link, renderMode?: string) => {
    if (renderMode === 'preview') return createDocumentLink(getDocument(link.url));
    // auto-message uses the dynamic field as of right now and only supports one attached form/packet per template
    return composerMode === 'auto-message'
      ? getWeaveFormDynamicField(textDynamicFields)
      : createDocumentLink(getDocument(link.url));
  };

  //////////////////////////////////////// PREFERENCE_MANAGEMENT_LINK //////////////////////////////////////////////
  const { data: preferenceLinks } = useGenerateManagementUrls({
    locationId,
    channel: Channel.CHANNEL_EMAIL,
    userChannelAddress: MOCK_PREFERENCE_TEST_EMAIL,
    messageType: MessageType_Enum.MARKETING_BULK_MESSAGES,
  });
  const managementUrl = preferenceLinks?.managementUrl ?? '';
  const getManagementUrl = (mode?: string) => {
    if (mode === 'preview') {
      return managementUrl;
    }

    return formatDynamicFieldKey(DynamicFieldKey_Enum.PREFERENCE_MANAGEMENT_LINK);
  };

  //////////////////////////////////////// SCHEDULE_LINK ///////////////////////////////////////////////
  const { data: onlineScheduleLink = '' } = useGetOnlineSchedulingLink({ locationId });
  const getOnlineScheduleLink = (mode?: string) => {
    if (mode === 'preview') {
      return onlineScheduleLink;
    }

    return formatDynamicFieldKey(DynamicFieldKey_Enum.SCHEDULE_LINK);
  };

  //////////////////////////////////////// UNSUBSCRIBE_LINK ///////////////////////////////////////////////
  const oneClickUrl = preferenceLinks?.oneClickUrl ?? '';
  const getOneClickUrl = (mode?: string) => {
    if (mode === 'preview') {
      return oneClickUrl;
    }

    return formatDynamicFieldKey(DynamicFieldKey_Enum.UNSUBSCRIBE_LINK);
  };

  const formatLink = (link: string, type?: FooterLinkType, mode?: string) => {
    if (type) {
      if (type === 'phone') {
        const onlyDigitsPhone = link.replace(/\D/g, '');
        const displayedPhone = onlyDigitsPhone ? onlyDigitsPhone : formattedPhone;
        return mode === 'preview'
          ? `tel:${displayedPhone}`
          : `tel:${formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_PHONE)}`;
      } else if (type === 'email') {
        return mode === 'preview'
          ? `mailto:${link}`
          : `mailto:${formatDynamicFieldKey(DynamicFieldKey_Enum.BUSINESS_GROUP_EMAIL)}`;
      }
    }
    return link.toLowerCase().indexOf('http') !== 0 ? 'https://' + link : link;
  };

  return {
    createFormLink,
    email,
    formatLink,
    getAddress,
    getAddressLink,
    getBusinessName,
    getCallToActionLink,
    getManagementUrl,
    getOneClickUrl,
    getOnlineScheduleLink,
    phone: formattedPhone,
  };
};
